// externals
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import * as dealHistorySelectors from '@makemydeal/dr-dash-store/selectors/dealHistorySelectors';
import * as navigationHooks from '@makemydeal/dr-dash-store/hooks/navigation';
import * as dealHistoryActionCreators from '@makemydeal/dr-dash-store/actions/dealHistoryActionCreator';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as unsavedChangesSelectors from '@makemydeal/dr-dash-store/selectors/unsavedChangesSelectors';
import { dealerSelectors, selectedOfferSelectors } from '@makemydeal/dr-shared-store';

// constants
import { RouteValues } from '@makemydeal/dr-dash-store/routeConstants';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

// components;
import { MakeCurrentModalInterstate, MakeCurrentButtonView } from '@makemydeal/dr-dash-components';

// styling
import { Grid } from '@interstate/components/Grid';
import { Button } from '@interstate/components/Button';
import { DealHistoryFooterContainer, DealHistoryFooterInterstateButtonContainer } from '../DealHistory.interstate.style';

type DealHistoryFooterInterstateProps = {
    onClose?: () => void;
    $isInDrawer?: boolean;
};

const DealHistoryFooterInterstate = ({ onClose, $isInDrawer = false }: DealHistoryFooterInterstateProps) => {
    const dispatch = useDispatch();
    const selectedDeal = useSelector(dealHistorySelectors.getSelectedDealBySelectedColumn);
    const navigateToDashboard = navigationHooks.useNavigate(RouteValues.DASHBOARD);
    const onCloseButtonClick = onClose || navigateToDashboard;
    const hasUnsavedChanges = useSelector(unsavedChangesSelectors.currentOfferHasUnsavedChanges);
    const dealXgId = useSelector(selectedOfferSelectors.getSelectedOfferDealXgId);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const dealerName = useSelector(dealerSelectors.getDealerName);

    const dispatchMakeCurrentOffer = useCallback(() => {
        if (selectedDeal) {
            if (hasUnsavedChanges) {
                dispatch(offerActionCreators.saveOffer({ isDraft: true, source: 'dealHistoryMakeCurrent' }));
            }
            dispatch(dealHistoryActionCreators.setMakeCurrentPending());
            const dealXgVersion = selectedDeal.offer.dealXgVersion;
            dispatch(dealHistoryActionCreators.makeCurrentDeal(dealXgVersion));
            logNewRelicPageAction('MV:DealHistory:MakeCurrentConfirmed', {
                source: $isInDrawer ? 'drawer' : 'page',
                dealXgId: dealXgId ?? '',
                dealerId,
                dealerName
            });
        }
        onCloseButtonClick();
    }, [dispatch, selectedDeal, onCloseButtonClick, hasUnsavedChanges, $isInDrawer, dealXgId, dealerId, dealerName]);

    return (
        <DealHistoryFooterContainer $isInDrawer={$isInDrawer}>
            <DealHistoryFooterInterstateButtonContainer $isInDrawer={$isInDrawer}>
                <Grid
                    container
                    justifyContent="flex-end"
                    gap={1}
                    paddingX={4}
                    paddingY={2}
                    bgcolor="#FFF"
                    borderTop="1px solid #B2B2B2"
                >
                    <Button
                        size="medium"
                        id="btnDealHistoryFooter"
                        data-testid="btn-dealHistory-footer"
                        buttonStyle="tertiary"
                        onClick={onCloseButtonClick}
                        className="btn-dealHistory-footer btn-link"
                    >
                        Cancel
                    </Button>
                    {selectedDeal && (
                        <MakeCurrentModalInterstate
                            onMakeCurrentOffer={dispatchMakeCurrentOffer}
                            makeCurrentButtonView={MakeCurrentButtonView.DealHistory}
                        />
                    )}
                </Grid>
            </DealHistoryFooterInterstateButtonContainer>
        </DealHistoryFooterContainer>
    );
};

export default DealHistoryFooterInterstate;
